import React, { useMemo } from 'react';
import moment from 'moment';
import { useShowContext } from 'framework/show';
import { DATE_FORMAT } from 'framework/utils';
import BalancePieChart from '../shared/BalancePieChart';
import useLeaveBalanceService from 'domain/people/employee/leavebalance/shared/balancesService';
import { ShowPortalLeaveType } from '../Show/type';

const PaidLeaveChart: React.FC = () => {
  const { data } = useShowContext<ShowPortalLeaveType>();
  const { paidBalances, currentDate } = data.getData();
  const { getCurrentPeriod } = useLeaveBalanceService();

  const COLORS = {
    used: 'var(--mui-palette-background-default)',
    currentYearAvailable: 'var(--mui-palette-success-main)',
    carryOver: 'var(--mui-palette-success-light)',
  };

  const periods = useMemo(() => {
    if (!paidBalances?.length) return [];

    return paidBalances.map((period: { startDate: string; endDate: string }) => ({
      value: `${period.startDate}_${period.endDate}`,
      label: `${period.startDate} - ${period.endDate}`,
      startDate: period.startDate,
      endDate: period.endDate,
    }));
  }, [paidBalances]);

  const currentPeriod = useMemo(() => getCurrentPeriod(periods), [getCurrentPeriod, periods]);

  const currentYearPeriod = useMemo(() => {
    if (!currentPeriod) return null;

    return paidBalances.find(
      (period: { startDate: string; endDate: string }) =>
        period.startDate === currentPeriod.startDate && period.endDate === currentPeriod.endDate,
    );
  }, [currentPeriod, paidBalances]);

  const nextPeriod = useMemo(() => {
    return paidBalances
      .filter((period: { startDate: string }) => moment(period.startDate).isAfter(moment(currentDate), 'day'))
      .sort(
        (a: { startDate: string }, b: { startDate: string }) =>
          moment(a.startDate).valueOf() - moment(b.startDate).valueOf(),
      )[0];
  }, [currentDate, paidBalances]);

  const balanceCalculations = useMemo(() => {
    if (!currentYearPeriod) {
      return {
        totalUsed: 0,
        availableCurrentYear: 0,
        availableCarryOver: 0,
        totalAvailable: 0,
      };
    }

    const currentYearBalance = Number(currentYearPeriod.paidLeavesTotalBalance || 0);
    const currentYearRemained = Number(currentYearPeriod.paidLeavesRemainedBalance || 0);
    const totalRemainedBalance = Number(currentYearPeriod.totalRemainedBalance || 0);
    const carryOverBalance = Math.max(0, totalRemainedBalance - currentYearRemained);
    const currentYearUsed = currentYearBalance - currentYearRemained;
    const usedFromCurrent = Math.min(currentYearUsed, currentYearBalance);
    const usedFromCarryOver = Math.max(0, currentYearUsed - usedFromCurrent);
    const totalUsed = usedFromCarryOver + usedFromCurrent;
    const availableCarryOver = Math.max(0, carryOverBalance - usedFromCarryOver);
    const availableCurrentYear = Math.max(0, currentYearRemained);

    return {
      totalUsed,
      availableCurrentYear,
      availableCarryOver,
      totalAvailable: availableCurrentYear + availableCarryOver,
    };
  }, [currentYearPeriod]);

  const chartData = useMemo(() => {
    const dayOnDemandRemainedBalance = Number(currentYearPeriod?.dayOnDemandsRemainedBalance || 0);
    const currentYearExtraLabel = currentYearPeriod?.hasDayOnDemandBalance
      ? `${balanceCalculations.availableCurrentYear}d / ${dayOnDemandRemainedBalance}d on demand`
      : '';

    return [
      { name: 'Used', value: balanceCalculations.totalUsed, color: COLORS.used },
      {
        name: 'Current year available balance',
        value: balanceCalculations.availableCurrentYear,
        color: COLORS.currentYearAvailable,
        extraLabel: currentYearExtraLabel,
      },
      { name: 'Available carry-over', value: balanceCalculations.availableCarryOver, color: COLORS.carryOver },
    ];
  }, [balanceCalculations, COLORS.carryOver, COLORS.currentYearAvailable, COLORS.used, currentYearPeriod]);

  if (!paidBalances?.length) return null;

  return (
    <BalancePieChart
      title="Paid leave balance"
      chartData={chartData}
      totalAvailable={balanceCalculations.totalAvailable}
      nextPeriodText={
        nextPeriod && Number(nextPeriod.paidLeavesRemainedBalance) ? (
          <>
            <strong>
              {nextPeriod.paidLeavesRemainedBalance}{' '}
              {Number(nextPeriod.paidLeavesRemainedBalance) === 1 ? 'day' : 'days'}
            </strong>{' '}
            of paid leave balance will become available on{' '}
            <strong>{moment(nextPeriod.startDate).format(DATE_FORMAT.NATURAL_SHORT)}</strong>
          </>
        ) : undefined
      }
    />
  );
};

export default PaidLeaveChart;
